import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateTimeFilters.css';
import { DATE_FILTERS, TIME_FILTERS } from '../../../../types/event.types';
import { formatDate } from '../../../../utils/dateUtils';
import { Icon } from '../../primitives/Icon';
import { Tag } from '../../primitives/Tags';
import { format } from 'date-fns';
import { useClickOutside } from '../../../../hooks/useClickOutside';

interface DateTimeFiltersProps {
  onDateFilterChange: (values: string[]) => void;
  onTimeFilterChange: (values: string[]) => void;
  selectedDates: string[];
  selectedTimes: string[];
  // Date range props
  dateRangeStart?: Date | null;
  dateRangeEnd?: Date | null;
  onDateRangeChange?: (start: Date | null, end: Date | null) => void;
  showDateRange?: boolean;
  // Hide today/tomorrow/weekend quick dates (mobile uses DateQuickSelectBar instead)
  hideQuickDates?: boolean;
}

const DateTimeFilters: React.FC<DateTimeFiltersProps> = ({
  onDateFilterChange,
  onTimeFilterChange,
  selectedDates = [],
  selectedTimes = [],
  dateRangeStart = null,
  dateRangeEnd = null,
  onDateRangeChange,
  showDateRange = false,
  hideQuickDates = false
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [isDateGroupAtEnd, setIsDateGroupAtEnd] = useState(false);
  const [isTimeGroupAtEnd, setIsTimeGroupAtEnd] = useState(false);
  const [isDateGroupAtStart, setIsDateGroupAtStart] = useState(true);
  const [isTimeGroupAtStart, setIsTimeGroupAtStart] = useState(true);

  // Date range state
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [tempRangeStart, setTempRangeStart] = useState<Date | null>(dateRangeStart);
  const [tempRangeEnd, setTempRangeEnd] = useState<Date | null>(dateRangeEnd);
  const dateRangeDropdownRef = useRef<HTMLDivElement>(null);

  const dateGroupRef = useRef<HTMLDivElement>(null);
  const timeGroupRef = useRef<HTMLDivElement>(null);

  // Sync temp values with props
  useEffect(() => {
    setTempRangeStart(dateRangeStart);
    setTempRangeEnd(dateRangeEnd);
  }, [dateRangeStart, dateRangeEnd]);

  // Close dropdown on click outside
  useClickOutside(dateRangeDropdownRef, () => setIsDateRangeOpen(false), isDateRangeOpen);

  // Date range helpers
  const hasDateRange = dateRangeStart || dateRangeEnd;

  const formatDateRangeLabel = () => {
    if (!dateRangeStart && !dateRangeEnd) return 'Date range';
    if (dateRangeStart && !dateRangeEnd) return `From ${format(dateRangeStart, 'MMM d')}`;
    if (!dateRangeStart && dateRangeEnd) return `Until ${format(dateRangeEnd, 'MMM d')}`;
    return `${format(dateRangeStart!, 'MMM d')} - ${format(dateRangeEnd!, 'MMM d')}`;
  };

  const handleDateRangeApply = () => {
    onDateRangeChange?.(tempRangeStart, tempRangeEnd);
    setIsDateRangeOpen(false);
  };

  const handleDateRangeClear = () => {
    setTempRangeStart(null);
    setTempRangeEnd(null);
    onDateRangeChange?.(null, null);
    setIsDateRangeOpen(false);
  };

  const checkIfAtEnd = (element: HTMLElement) => {
    const isAtEnd = Math.abs(
      element.scrollWidth - element.clientWidth - element.scrollLeft
    ) < 1;
    return isAtEnd;
  };

  const checkIfAtStart = (element: HTMLElement) => {
    return element.scrollLeft <= 1;
  };

  const handleScroll = (
    element: HTMLElement, 
    setIsAtEnd: (isAtEnd: boolean) => void,
    setIsAtStart: (isAtStart: boolean) => void
  ) => {
    setIsAtEnd(checkIfAtEnd(element));
    setIsAtStart(checkIfAtStart(element));
  };

  useEffect(() => {
    const dateGroup = dateGroupRef.current;
    const timeGroup = timeGroupRef.current;

    if (dateGroup) {
      handleScroll(dateGroup, setIsDateGroupAtEnd, setIsDateGroupAtStart);
      dateGroup.addEventListener('scroll', () =>
        handleScroll(dateGroup, setIsDateGroupAtEnd, setIsDateGroupAtStart)
      );
    }

    if (timeGroup) {
      handleScroll(timeGroup, setIsTimeGroupAtEnd, setIsTimeGroupAtStart);
      timeGroup.addEventListener('scroll', () =>
        handleScroll(timeGroup, setIsTimeGroupAtEnd, setIsTimeGroupAtStart)
      );
    }

    return () => {
      if (dateGroup) {
        dateGroup.removeEventListener('scroll', () =>
          handleScroll(dateGroup, setIsDateGroupAtEnd, setIsDateGroupAtStart)
        );
      }
      if (timeGroup) {
        timeGroup.removeEventListener('scroll', () =>
          handleScroll(timeGroup, setIsTimeGroupAtEnd, setIsTimeGroupAtStart)
        );
      }
    };
  }, []);

  const handleDateChange = (value: string) => {
    console.log('🗓️ handleDateChange - value:', value);
    console.log('🗓️ Current selectedDates:', selectedDates);

    if (value === 'custom') {
      setIsDatePickerOpen(true);
      return;
    }

    let newValues: string[];
    if (selectedDates.includes(value)) {
      // Si déjà sélectionné, on le retire
      newValues = selectedDates.filter(date => date !== value);
    } else {
      // Sinon on l'ajoute
      newValues = [...selectedDates, value];
    }
    
    console.log('🗓️ New selectedDates:', newValues);
    onDateFilterChange(newValues);
    if (newValues.length === 0) {
      setCustomDate(null);
    }
  };

  const handleTimeChange = (value: string) => {
    let newValues: string[];
    
    if (selectedTimes.includes(value)) {
      // Si le filtre est déjà sélectionné, on le retire
      newValues = selectedTimes.filter(time => time !== value);
    } else {
      // Si on essaie d'ajouter le dernier filtre non sélectionné
      if (selectedTimes.length === TIME_FILTERS.length - 1) {
        // On désélectionne tout
        newValues = [];
      } else {
        // Sinon on l'ajoute normalement
        newValues = [...selectedTimes, value];
      }
    }
    
    onTimeFilterChange(newValues);
  };

  const handleCustomDateChange = (date: Date | null) => {
    console.log('📅 handleCustomDateChange - date:', date);
    if (date) {
      const customValue = `custom_${formatDate(date)}`;
      console.log('📅 Formatted custom date:', customValue);
      
      // Si la date est déjà sélectionnée, on la retire
      if (selectedDates.includes(customValue)) {
        console.log('📅 Date already selected, removing it');
        setCustomDate(null);
        const newDates = selectedDates.filter(d => d !== customValue);
        onDateFilterChange(newDates);
      } else {
        // Sinon on l'ajoute
        setCustomDate(date);
        const newDates = [...selectedDates.filter(d => !d.startsWith('custom_')), customValue];
        console.log('📅 Final dates array:', newDates);
        onDateFilterChange(newDates);
      }
      setIsDatePickerOpen(false);
    }
  };

  const handleCustomDateClick = () => {
    // Si une date personnalisée est déjà sélectionnée, on la retire
    if (selectedDates.some(d => d.startsWith('custom_'))) {
      setCustomDate(null);
      const newDates = selectedDates.filter(d => !d.startsWith('custom_'));
      onDateFilterChange(newDates);
      return;
    }

    if ('ontouchstart' in window) {
      const input = document.createElement('input');
      input.type = 'date';
      input.min = new Date().toISOString().split('T')[0];
      input.value = customDate ? formatDate(customDate) : '';

      // Off-screen but interactive (pointer-events must NOT be none)
      input.style.position = 'fixed';
      input.style.top = '-100px';
      input.style.left = '-100px';
      input.style.opacity = '0';

      const handleChange = (e: Event) => {
        const selectedDate = (e.target as HTMLInputElement).value;
        if (selectedDate) {
          handleCustomDateChange(new Date(selectedDate + 'T12:00:00'));
        }
        cleanup();
      };

      const cleanup = () => {
        input.removeEventListener('change', handleChange);
        if (document.body.contains(input)) {
          document.body.removeChild(input);
        }
      };

      input.addEventListener('change', handleChange);
      document.body.appendChild(input);

      // showPicker() is more reliable than focus+click on modern mobile browsers
      try {
        (input as any).showPicker();
      } catch {
        input.focus();
        input.click();
      }

      // Cleanup if user cancels (no change event)
      setTimeout(() => cleanup(), 60000);
    } else {
      setIsDatePickerOpen(true);
    }
  };

  return (
    <div className="datetime-filter">
      {/* Section Date */}
      <div className="datetime-filter__section">
        <div className="datetime-filter__group-wrapper">
          <div 
            ref={dateGroupRef}
            className={`datetime-filter__group ${isDateGroupAtEnd ? 'at-end' : ''} ${!isDateGroupAtStart ? 'scrolled' : ''}`}
          >
            {(hideQuickDates
              ? DATE_FILTERS.filter(f => f.value === 'custom')
              : DATE_FILTERS
            ).map((filter) => (
              filter.value === 'custom' ? (
                <button
                  key={filter.id}
                  className={`datetime-filter__button ${selectedDates.some(d => d.startsWith('custom_')) ? 'selected' : ''}`}
                  onClick={handleCustomDateClick}
                  data-custom="true"
                >
                  <Tag
                    size="lg"
                    label={
                      <>
                        <Icon name="Calendar" size={16} className="datetime-filter__time-icon" />
                        {selectedDates.some(d => d.startsWith('custom_'))
                          ? selectedDates.find(d => d.startsWith('custom_'))?.split('_')[1]
                          : 'Select a date'}
                      </>
                    }
                  />
                </button>
              ) : (
                <button
                  key={filter.id}
                  className={`datetime-filter__button ${selectedDates.includes(filter.value) ? 'selected' : ''}`}
                  onClick={() => handleDateChange(filter.value)}
                >
                  <Tag
                    label={filter.label}
                    size="lg"
                  />
                </button>
              )
            ))}
            {/* Date Range - same structure as other tags */}
            {showDateRange && (
              <button
                type="button"
                className={`datetime-filter__button ${hasDateRange ? 'selected' : ''}`}
                onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}
              >
                <Tag
                  size="lg"
                  label={
                    <>
                      <Icon name="CalendarRange" size={16} className="datetime-filter__time-icon" />
                      {formatDateRangeLabel()}
                    </>
                  }
                />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Date Range Dropdown - positioned outside the group */}
      {showDateRange && isDateRangeOpen && (
        <div className="datetime-filter__date-range-dropdown" ref={dateRangeDropdownRef}>
          <div className="datetime-filter__date-range-inputs">
            <div className="datetime-filter__date-range-field">
              <label>From</label>
              <input
                type="date"
                value={tempRangeStart ? format(tempRangeStart, 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : null;
                  setTempRangeStart(date);
                }}
              />
            </div>
            <div className="datetime-filter__date-range-field">
              <label>To</label>
              <input
                type="date"
                value={tempRangeEnd ? format(tempRangeEnd, 'yyyy-MM-dd') : ''}
                min={tempRangeStart ? format(tempRangeStart, 'yyyy-MM-dd') : undefined}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : null;
                  setTempRangeEnd(date);
                }}
              />
            </div>
          </div>
          <div className="datetime-filter__date-range-actions">
            <button
              type="button"
              className="datetime-filter__date-range-btn datetime-filter__date-range-btn--clear"
              onClick={handleDateRangeClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="datetime-filter__date-range-btn datetime-filter__date-range-btn--apply"
              onClick={handleDateRangeApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Separator */}
      <div className="datetime-filter__separator" />

      {/* Section Heure */}
      <div className="datetime-filter__section">
        <div className="datetime-filter__group-wrapper">
          <div
            ref={timeGroupRef}
            className={`datetime-filter__group ${isTimeGroupAtEnd ? 'at-end' : ''} ${!isTimeGroupAtStart ? 'scrolled' : ''}`}
          >
            {TIME_FILTERS.map((filter) => (
              <button
                key={filter.id}
                className={`datetime-filter__button ${selectedTimes.includes(filter.value) ? 'selected' : ''}`}
                onClick={() => handleTimeChange(filter.value)}
              >
                <Tag
                  size="lg"
                  label={
                    <>
                      <Icon
                        name={filter.value === 'morning' ? 'Sunrise' :
                              filter.value === 'afternoon' ? 'Sun' :
                              'Sunset'}
                        size={16}
                        className="datetime-filter__time-icon"
                      />
                      {filter.label}
                    </>
                  }
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* DatePicker Modal */}
      {isDatePickerOpen && window.innerWidth > 768 && (
        <>
          <div 
            className="datetime-filter__picker-overlay"
            onClick={() => setIsDatePickerOpen(false)}
          />
          <div className="datetime-filter__picker-container">
            <button 
              className="datetime-filter__picker-close"
              onClick={() => setIsDatePickerOpen(false)}
            >
              ×
            </button>
            <DatePicker
              selected={customDate}
              onChange={handleCustomDateChange}
              inline
              minDate={new Date()}
              dateFormat="yyyy-MM-dd"
              calendarClassName="custom-calendar"
              monthsShown={1}
              showPopperArrow={false}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DateTimeFilters;